import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useDynamicPolling,
  useNavigationHandlers,
  useScrollHandler,
} from '@src/shared/hooks';
import { useModalControls } from './use-model-controls';
import { setAuthToken } from '@src/config/api';
import { goToByError } from '@src/shared/utils';
import {
  setConfirmed,
  clearOptions,
  getSettings,
  getProposal,
  generateCode,
  checkProposal,
  setProposalId,
  setEtapa,
} from '@src/shared/zustand';
import {
  useConfirmedState,
  useSelectedLoanSimulationState,
  useProposalState,
  useSettingsState,
  useGTM,
} from '@src/shared/hooks';
import { CheckProposalStatus } from '@src/shared/models';
import { GTMEvents } from '@src/config/gtm';
import { Etapa } from '@src/shared/models/etapa';

export const useProposalController = () => {
  const { confirmed } = useConfirmedState();
  const { loanSimulation } = useSelectedLoanSimulationState();
  const { proposal, loading } = useProposalState();
  const { idwallPoolingIntervals } = useSettingsState();

  const params = useParams();
  const navigationHandlers = useNavigationHandlers();
  const modalControls = useModalControls();
  const { isFixed, scrollToTop } = useScrollHandler(
    modalControls.openModelMoreDetails
  );
  const { trackEvent } = useGTM();

  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [finishLoading, setFinishLoading] = useState(false);
  const [idwallQueryTracked, setIdwallQueryTracked] = useState(false);

  const hash = params['hash'];
  const proposalId = params['proposal'];

  // Configuração do pooling dinâmico
  const { startPolling, stopPolling, isPolling } = useDynamicPolling({
    intervals: idwallPoolingIntervals,
    pollFunction: async () => {
      return new Promise<CheckProposalStatus>((resolve, reject) => {
        checkProposal(proposalId, (error, status) => {
          if (error) {
            reject(error);
          } else {
            resolve(status);
          }
        });
      });
    },
    shouldContinue: (result) => {
      return result === CheckProposalStatus.PENDING;
    },
    onSuccess: (result) => {
      if (result === CheckProposalStatus.APPROVED) {
        setFinishLoading(true);
      }

      if (result === CheckProposalStatus.REJECTED) {
        setShowLoading(false);
        navigationHandlers.toReprovedByIdwall();

        return;
      }
    },
    onError: (error) => {
      console.error(error);
      setShowLoading(false);
      goToByError(error as any, navigationHandlers);
    },
    onTimeout: () => {
      setShowLoading(false);
      trackEvent(GTMEvents.IDWALL_TIMEOUT, {
        proposal_id: proposalId,
      });
      navigationHandlers.toTimeoutIdwall();
    },
    onInterval: (interval) => {
      if ([1, 2].includes(interval)) {
        setCurrentImageIndex(interval);
      }
    },
  });

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1025) setConfirmed(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startIdwallPooling = () => {
    if (idwallPoolingIntervals?.length > 0) {
      if (showSplashScreen) {
        setShowSplashScreen(false);
      }

      if (!showLoading) {
        setShowLoading(true);
      }

      if (!isPolling) {
        if (!idwallQueryTracked) {
          trackEvent(GTMEvents.IDWALL_QUERY, {
            proposal_id: proposalId,
          });
          setIdwallQueryTracked(true);
        }

        startPolling();
      }
    }
  };

  useEffect(() => {
    if (
      proposal &&
      Object.keys(proposal).length > 1 &&
      proposal.proposalId === proposalId
    ) {
      setShowSplashScreen(false);
      setEtapa(Etapa.VIEW_INSTALLMENTS);
      return;
    }

    if (hash) {
      (async () => {
        await clearOptions();
        await setAuthToken(hash);
        await setProposalId(proposalId);
        await setEtapa(Etapa.IDWALL);

        getSettings((error, state) => {
          if (error) {
            console.error(error);
            goToByError(error, navigationHandlers);

            return;
          }

          if (state.idwallPoolingIntervals?.length > 0) {
            startIdwallPooling();
          }
        });
      })();
    }

    return () => {
      stopPolling();
    };
  }, [hash, proposalId]);

  const toContinue = () => {
    setEtapa(Etapa.REVIEW_INSTALLMENTS);

    trackEvent(GTMEvents.REVIEW_INSTALLMENTS, {
      proposal_id: proposal.proposalId,
      valor_viagem: loanSimulation?.totalPriceOfFlightTicket,
      qtd_parcelas: loanSimulation?.schedule.length,
      valor_parcelas: loanSimulation?.schedule[0].payment,
      valor_total: loanSimulation?.totalAmountOwedWithDownPayment,
      valor_entrada: loanSimulation?.downPayment,
    });

    navigationHandlers.toConfirmed();
  };

  const toGenerateCode = async () => {
    trackEvent(GTMEvents.SELECT_INSTALLMENTS, {
      proposal_id: proposal.proposalId,
      qtd_parcelas: loanSimulation?.schedule.length,
      valor_parcelas: loanSimulation?.schedule[0].payment,
      valor_total: loanSimulation?.totalAmountOwedWithDownPayment,
      valor_entrada: loanSimulation?.downPayment,
      valor_viagem: loanSimulation?.totalPriceOfFlightTicket,
    });

    generateCode(proposal.personalData.email, (error) => {
      if (error) {
        goToByError(error, navigationHandlers);

        return;
      }

      navigationHandlers.toEmailValidation();
    });
  };

  const onFinishLoading = () => {
    setShowLoading(false);
    getProposal(proposalId, (error) => {
      if (error) {
        goToByError(error, navigationHandlers);

        return;
      }

      setEtapa(Etapa.VIEW_INSTALLMENTS);
      trackEvent(GTMEvents.VIEW_INSTALLMENTS, {
        proposal_id: proposal.proposalId,
        valor_viagem: loanSimulation?.totalPriceOfFlightTicket,
      });
    });
  };

  return {
    ...navigationHandlers,
    ...modalControls,
    toGenerateCode,
    toContinue,
    loading,
    confirmed,
    isFixed,
    proposal,
    loanSimulation,
    validateForm: modalControls.terms && modalControls.privatePolicy,
    showSplashScreen,
    showLoading,
    currentImageIndex,
    finishLoading,
    onFinishLoading,
    startIdwallPooling,
  };
};
